export interface NewsletterData {
  emailSubject: string;
  emailSubheading: string;
  newsletterDate: string;
  logoUrl: string;
  downloadUrlAppStore: string;
  downloadUrlGooglePlay: string;
  leadArticle: {
    title: string;
    imageUrl: string;
    text: string[];
    links: { text: string; url: string }[];
  };
  features: {
    featureType: string;
    sourceId: string;
    title: string;
    imageUrl: string;
    description: string;
    featureText: string;
    featureTag: string;
    displayOrder: number;
    tags: string[];
  }[];
  eventCalendar: {
    eventId: string;
    eventType: string;
    title: string;
    dateTime: string;
    subtitle: string;
    imageUrl: string;
  }[];
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function replaceSimplePlaceholders(template: string, data: Record<string, any>, prefix = ''): string {
  let result = template;
  for (const [key, value] of Object.entries(data)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string' || typeof value === 'number') {
      const escapedKey = fullKey.replace(/\./g, '\\.');
      const rawRegex = new RegExp(`\\{\\{\\{\\s*${escapedKey}\\s*\\}\\}\\}`, 'g');
      result = result.replace(rawRegex, String(value));
      const regex = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g');
      result = result.replace(regex, escapeHtml(String(value)));
    }
  }
  return result;
}

function processLoops(template: string, data: Record<string, any>): string {
  let result = template;

  const loopRegex = /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g;
  let match;

  while ((match = loopRegex.exec(result)) !== null) {
    const [fullMatch, key, innerTemplate] = match;
    const items = data[key];

    if (Array.isArray(items) && items.length > 0) {
      const rendered = items.map((item, index) => {
        let itemHtml = innerTemplate;
        itemHtml = replaceSimplePlaceholders(itemHtml, { ...item, _index: index + 1 });

        if (Array.isArray(item.text)) {
          const textLoopRegex = /\{\{#text\}\}([\s\S]*?)\{\{\/text\}\}/g;
          let textMatch;
          while ((textMatch = textLoopRegex.exec(itemHtml)) !== null) {
            const textInner = textMatch[1];
            const textRendered = item.text.map((t: string) => textInner.replace(/\{\{\s*\.\s*\}\}/g, escapeHtml(t))).join('');
            itemHtml = itemHtml.replace(textMatch[0], textRendered);
          }
        }

        if (Array.isArray(item.links)) {
          const linksLoopRegex = /\{\{#links\}\}([\s\S]*?)\{\{\/links\}\}/g;
          let linksMatch;
          while ((linksMatch = linksLoopRegex.exec(itemHtml)) !== null) {
            const linksInner = linksMatch[1];
            const linksRendered = item.links.map((link: any) => replaceSimplePlaceholders(linksInner, link)).join('');
            itemHtml = itemHtml.replace(linksMatch[0], linksRendered);
          }
        }

        if (Array.isArray(item.tags)) {
          const tagsLoopRegex = /\{\{#tags\}\}([\s\S]*?)\{\{\/tags\}\}/g;
          let tagsMatch;
          while ((tagsMatch = tagsLoopRegex.exec(itemHtml)) !== null) {
            const tagsInner = tagsMatch[1];
            const tagsRendered = item.tags.map((tag: string) => tagsInner.replace(/\{\{\s*\.\s*\}\}/g, escapeHtml(tag))).join('');
            itemHtml = itemHtml.replace(tagsMatch[0], tagsRendered);
          }
        }

        return itemHtml;
      }).join('');
      result = result.replace(fullMatch, rendered);
    } else {
      result = result.replace(fullMatch, '');
    }

    loopRegex.lastIndex = 0;
  }

  return result;
}

export function renderNewsletter(template: string, data: NewsletterData): string {
  let result = template;

  result = replaceSimplePlaceholders(result, {
    emailSubject: data.emailSubject,
    emailSubheading: data.emailSubheading,
    newsletterDate: data.newsletterDate,
    logoUrl: data.logoUrl,
    downloadUrlAppStore: data.downloadUrlAppStore,
    downloadUrlGooglePlay: data.downloadUrlGooglePlay,
  });

  result = replaceSimplePlaceholders(result, data.leadArticle, 'leadArticle');

  const leadTextLoop = /\{\{#leadArticle\.text\}\}([\s\S]*?)\{\{\/leadArticle\.text\}\}/g;
  let textMatch;
  while ((textMatch = leadTextLoop.exec(result)) !== null) {
    const inner = textMatch[1];
    const rendered = data.leadArticle.text.map(t => inner.replace(/\{\{\s*\.\s*\}\}/g, escapeHtml(t))).join('');
    result = result.replace(textMatch[0], rendered);
    leadTextLoop.lastIndex = 0;
  }

  const leadLinksLoop = /\{\{#leadArticle\.links\}\}([\s\S]*?)\{\{\/leadArticle\.links\}\}/g;
  let linksMatch;
  while ((linksMatch = leadLinksLoop.exec(result)) !== null) {
    const inner = linksMatch[1];
    const rendered = data.leadArticle.links.map(link => replaceSimplePlaceholders(inner, link)).join('');
    result = result.replace(linksMatch[0], rendered);
    leadLinksLoop.lastIndex = 0;
  }

  result = processLoops(result, {
    features: data.features.sort((a, b) => a.displayOrder - b.displayOrder),
    eventCalendar: data.eventCalendar,
  });

  return result;
}

export const DEFAULT_NEWSLETTER_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{emailSubject}}</title>
  <style>
    body { margin: 0; padding: 0; background-color: #f7f5f2; font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #3d3d3d; }
    .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; margin-top: 20px; margin-bottom: 20px; }
    .header { background-color: #2d2d44; color: #ffffff; padding: 40px 30px 10px; text-align: center; }
    .header h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.3px; }
    .header p { margin: 10px 0 0; font-size: 14px; color: #c8c8da; font-weight: 300; }
    .newsletter-date { font-size: 12px; color: #a0a0b8; margin-top: 8px; letter-spacing: 0.5px; text-transform: uppercase; }
    .lead-image-wrapper { margin: 0; line-height: 0; background-color: #2d2d44; }
    .lead-image-wrapper img { display: block; width: 100%; max-height: 300px; object-fit: cover; }
    .section { padding: 28px 30px; }
    .section-title { font-size: 13px; font-weight: 600; color: #8a8a9a; text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 18px; }
    .lead-article-text { padding: 24px 30px 28px; }
    .lead-article-text h2 { font-size: 20px; color: #2d2d44; margin: 0 0 14px; font-weight: 600; line-height: 1.3; }
    .lead-article-text p { font-size: 15px; line-height: 1.7; color: #555555; margin: 0 0 10px; }
    .lead-article-text .links { margin-top: 18px; }
    .lead-article-text .links a { display: inline-block; color: #6366f1; text-decoration: none; margin-right: 16px; font-size: 14px; font-weight: 500; }
    .divider { height: 1px; background: linear-gradient(to right, transparent, #e5e3df, transparent); margin: 0 30px; }
    .feature-item { padding: 18px 0; }
    .feature-item-inner { display: flex; gap: 16px; align-items: flex-start; }
    .feature-item img { width: 100px; height: 72px; object-fit: cover; border-radius: 10px; flex-shrink: 0; }
    .feature-item .feature-content { flex: 1; }
    .feature-item .feature-tag { display: inline-block; background-color: #f0edfa; color: #6366f1; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 12px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
    .feature-item h3 { margin: 0 0 4px; font-size: 15px; color: #2d2d44; font-weight: 600; }
    .feature-item p { margin: 0; font-size: 13px; color: #777777; line-height: 1.5; }
    .feature-separator { height: 1px; background-color: #f0eeeb; margin: 0; }
    .event-item { padding: 14px 0; }
    .event-item-inner { display: flex; gap: 14px; align-items: center; }
    .event-item img { width: 64px; height: 64px; object-fit: cover; border-radius: 10px; flex-shrink: 0; }
    .event-item .event-content { flex: 1; }
    .event-item h4 { margin: 0 0 3px; font-size: 14px; color: #2d2d44; font-weight: 600; }
    .event-item .event-date { font-size: 12px; color: #888888; }
    .event-item .event-type { display: inline-block; background-color: #fef7ec; color: #b45309; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 12px; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
    .event-separator { height: 1px; background-color: #f0eeeb; margin: 0; }
    .app-section { text-align: center; padding: 24px 30px 28px; background-color: #faf9f7; }
    .app-section .section-title { margin-bottom: 14px; }
    .footer-bridge { background-color: #2d2d44; height: 12px; margin: 0; }
    .footer { background-color: #2d2d44; color: #8a8a9a; padding: 24px 30px; text-align: center; font-size: 12px; border-radius: 0 0 16px 16px; }
    .footer p { margin: 0; }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      <img src="{{{logoUrl}}}" alt="Wellbound+" style="width: 56px; height: 56px; margin-bottom: 14px; border-radius: 14px;" />
      <h1>{{emailSubject}}</h1>
      <p>{{emailSubheading}}</p>
      <div class="newsletter-date">{{newsletterDate}}</div>
    </div>
    <!-- Lead Article Image -->
    <div class="lead-image-wrapper">
      <img src="{{{leadArticle.imageUrl}}}" alt="{{leadArticle.title}}" />
    </div>

    <!-- Lead Article Text -->
    <div class="lead-article-text">
      <h2>{{leadArticle.title}}</h2>
      {{#leadArticle.text}}
      <p>{{.}}</p>
      {{/leadArticle.text}}
      <div class="links">
        {{#leadArticle.links}}
        <a href="{{url}}">{{text}} &rarr;</a>
        {{/leadArticle.links}}
      </div>
    </div>

    <div class="divider"></div>

    <!-- Features -->
    <div class="section">
      <div class="section-title">Featured</div>
      {{#features}}
      <div class="feature-item">
        <div class="feature-item-inner">
          <img src="{{{imageUrl}}}" alt="{{title}}" />
          <div class="feature-content">
            <span class="feature-tag">{{featureTag}}</span>
            <h3>{{title}}</h3>
            <p>{{featureText}}</p>
          </div>
        </div>
      </div>
      <div class="feature-separator"></div>
      {{/features}}
    </div>

    <div class="divider"></div>

    <!-- Event Calendar -->
    <div class="section">
      <div class="section-title">Upcoming Events</div>
      {{#eventCalendar}}
      <div class="event-item">
        <div class="event-item-inner">
          <img src="{{{imageUrl}}}" alt="{{title}}" />
          <div class="event-content">
            <span class="event-type">{{eventType}}</span>
            <h4>{{title}}</h4>
            <div class="event-date">{{dateTime}}</div>
          </div>
        </div>
      </div>
      <div class="event-separator"></div>
      {{/eventCalendar}}
    </div>

    <!-- Download App -->
    <div class="app-section">
      <div class="section-title">Get the App</div>
      <div style="display: inline-block;">
        <a href="{{{downloadUrlAppStore}}}" style="display: inline-block; margin: 0 6px;" target="_blank">
          <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="Download on the App Store" style="height: 40px; border-radius: 8px;" />
        </a>
        <a href="{{{downloadUrlGooglePlay}}}" style="display: inline-block; margin: 0 6px;" target="_blank">
          <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" style="height: 58px; margin-top: -9px; margin-bottom: -9px;" />
        </a>
      </div>
    </div>

    <!-- Footer bridge: straight-edge colour transition from app section to footer -->
    <div class="footer-bridge"></div>

    <!-- Footer -->
    <div class="footer">
      <p>&copy; Wellbound. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
